import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';

import { GAME_TYPES } from '@/features/gameSession/store/constants';
import { GameAudioProvider } from '@/shared/hooks/useGameAudioSettings';
import useLaneChooserGameplayAudio from '@/features/laneChooser/hooks/useLaneChooserGameplayAudio';
import useLaneChooserQuestion from '@/features/laneChooser/hooks/useLaneChooserQuestion';
import { EndOutcome } from '@/features/gameSession/types';
import { LaneChooserThemeProvider } from '@/features/laneChooser/theme';

import EndScreen from '@/features/laneChooser/components/stages/EndScreen';
import GameScreen from '@/features/laneChooser/components/stages/GameScreen';
import StartScreen from '@/features/laneChooser/components/stages/StartScreen';
import GameStyleBoundary from '@/shared/components/gameStyleBoundary/GameStyleBoundary';

import '@/features/laneChooser/styles/lane-chooser.scss';
import '@/features/laneChooser/styles/lane-chooser-themes.scss';

import type {
	GameAnswerEvent,
	GameAudioProps,
	GameCloseEvent,
	GameData,
	GameFinishEvent,
	Question,
} from '@/features/gameSession/types';
import type { LaneChooserAnswerResolution, LaneChooserQuestionPayload } from '@/features/laneChooser/game/types';

export type LaneChooserGameProps = {
	gameData?: GameData | null;
	questions?: Question[];
	themeKey?: string | null;
	onAnswer?: (event: GameAnswerEvent) => void;
	onFinish?: (event: GameFinishEvent) => void;
	onClose: (event?: GameCloseEvent) => void;
	onImagePreview?: (imageSrc: string) => void;
	onPreviewImage?: (imageSrc: string) => void;
} & GameAudioProps;

const DEFAULT_LANE_LINES_COUNT = 4;

type LaneChooserGameInnerProps = Omit<LaneChooserGameProps, 'audioOn' | 'onAudioChange'>;

const LaneChooserGameInner = ({
	gameData = null,
	questions = [],
	onAnswer,
	onFinish,
	onClose,
	onImagePreview,
	onPreviewImage,
}: LaneChooserGameInnerProps) => {
	const [showStartScreen, setShowStartScreen] = useState(true);
	const [gameVisible, setGameVisible] = useState(false);
	const [gameStarted, setGameStarted] = useState(false);
	const [endOutcome, setEndOutcome] = useState<EndOutcome | null>(null);
	const [answers, setAnswers] = useState<boolean[]>([]);
	const [timeAdjustSeconds, setTimeAdjustSeconds] = useState(0);
	const [correctStreak, setCorrectStreak] = useState(Number(gameData?.game?.correct_answer_streak || 0));
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const [questionStartTs, setQuestionStartTs] = useState(Date.now());
	const [answerResolution, setAnswerResolution] = useState<LaneChooserAnswerResolution | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const finishEmittedRef = useRef(false);
	const { playAnswerFeedback, playSoundEffect } = useLaneChooserGameplayAudio({
		active: !showStartScreen && !endOutcome,
	});

	const currentQuestion = questions[currentQuestionIndex];
	const { details: questionDetails, submitAnswer } = useLaneChooserQuestion({
		question: currentQuestion,
		questionStartTs,
	});
	const {
		answerCorrectFlags,
		answerLabels,
		imageUrls,
		question: sourceQuestion,
		questionHasImage,
		questionHtml,
		titleText,
	} = questionDetails;

	const totalPlayTime = Number(gameData?.game?.total_time_in_seconds || 0);
	const laneLinesCount = Math.max(
		1,
		Math.min(DEFAULT_LANE_LINES_COUNT, answerLabels.length || DEFAULT_LANE_LINES_COUNT),
	);
	const questionPayload = useMemo<LaneChooserQuestionPayload | null>(() => {
		if (!sourceQuestion) {
			return null;
		}

		return {
			title: titleText,
			questionHtml,
			imageUrls,
			questionHasImage,
			answerLabels,
		};
	}, [answerLabels, imageUrls, questionHasImage, questionHtml, sourceQuestion, titleText]);

	const emitFinish = useCallback(
		(event: GameFinishEvent) => {
			if (finishEmittedRef.current) {
				return;
			}

			finishEmittedRef.current = true;
			onFinish?.(event);
		},
		[onFinish],
	);

	const handleGameStart = useCallback(() => {
		setQuestionStartTs(Date.now());
		setGameStarted(true);
	}, []);

	const handleAnswerRequest = useCallback(
		(selectedIndex: number) => {
			if (!currentQuestion || endOutcome || answerResolution) {
				return;
			}

			const submission = submitAnswer(selectedIndex);
			if (!submission) {
				return;
			}

			const isLastQuestion = currentQuestionIndex >= questions.length - 1;
			const { answer, answerIndex, elapsedSeconds, isCorrect } = submission;
			const nextCorrectStreak = isCorrect ? correctStreak + 1 : 0;
			const deltaSeconds = isCorrect
				? gameData?.game?.reward?.correct_answer_streak?.[nextCorrectStreak] ?? 0
				: gameData?.game?.reward?.wrong_answer_streak?.[1] ?? 0;
			const correctIndex = answerCorrectFlags.findIndex(Boolean);

			onAnswer?.({
				gameType: GAME_TYPES.LANE_CHOOSER_GAME,
				question: currentQuestion,
				questionIndex: currentQuestionIndex,
				isCorrect,
				answer,
				answerIndex,
				answerId: answer.id,
				answerTexts: [answer.answer_display ?? answer.answer_text ?? answer.content ?? ''],
				answerTimeInSeconds: elapsedSeconds,
				data: {
					selectedIndex,
				},
				finishEvent: isLastQuestion
					? {
							gameOutcome: EndOutcome.SUCCEEDED,
					  }
					: undefined,
			});

			if (isLastQuestion) {
				if (onAnswer) {
					finishEmittedRef.current = true;
				} else {
					emitFinish({ gameOutcome: EndOutcome.SUCCEEDED });
				}
			}

			playAnswerFeedback(isCorrect);

			setAnswers((previousAnswers) => [...previousAnswers, isCorrect]);
			setCorrectStreak(nextCorrectStreak);
			setTimeAdjustSeconds((previousSeconds) => previousSeconds + deltaSeconds);
			setAnswerResolution({
				selectedIndex,
				correctIndex: correctIndex >= 0 ? correctIndex : null,
				isCorrect,
				deltaSeconds,
			});
		},
		[
			answerCorrectFlags,
			answerResolution,
			correctStreak,
			currentQuestion,
			currentQuestionIndex,
			emitFinish,
			endOutcome,
			gameData?.game?.reward?.correct_answer_streak,
			gameData?.game?.reward?.wrong_answer_streak,
			onAnswer,
			playAnswerFeedback,
			questions.length,
			submitAnswer,
		],
	);

	const handleRoundExitComplete = useCallback(() => {
		setAnswerResolution(null);

		if (currentQuestionIndex >= questions.length - 1) {
			setEndOutcome(EndOutcome.SUCCEEDED);
			setGameStarted(false);
			return;
		}

		setCurrentQuestionIndex((previousIndex) => Math.min(previousIndex + 1, Math.max(0, questions.length - 1)));
		setQuestionStartTs(Date.now());
	}, [currentQuestionIndex, questions.length]);

	const handleTimeOut = useCallback(() => {
		if (endOutcome || finishEmittedRef.current) {
			return;
		}

		setEndOutcome(EndOutcome.LOST);
		setGameStarted(false);
	}, [endOutcome]);

	const handleGameplayBack = useCallback(() => {
		onClose(
			answers.length > 0
				? {
						finishEvent: {
							gameOutcome: EndOutcome.ABANDONED,
						},
				  }
				: undefined,
		);
	}, [answers.length, onClose]);

	useEffect(() => {
		if (showStartScreen) {
			return;
		}

		const container = containerRef.current;
		setGameVisible(true);
		if (container) {
			gsap.fromTo(container, { opacity: 0 }, { opacity: 1, duration: 0.35, ease: 'power1.out' });
		}
	}, [showStartScreen]);

	useEffect(() => {
		setCorrectStreak(Number(gameData?.game?.correct_answer_streak || 0));
	}, [gameData?.game?.correct_answer_streak]);

	useEffect(() => {
		if (endOutcome === EndOutcome.LOST && answers.length > 0) {
			emitFinish({ gameOutcome: EndOutcome.LOST });
		}
	}, [answers.length, emitFinish, endOutcome]);

	if (showStartScreen) {
		return <StartScreen onStart={() => setShowStartScreen(false)} onBack={() => onClose()} />;
	}

	if (endOutcome) {
		return <EndScreen outcome={endOutcome} onClose={() => onClose()} />;
	}

	return (
		<GameScreen
			ref={containerRef}
			gameVisible={gameVisible}
			questionsCount={questions.length}
			totalPlayTime={totalPlayTime}
			currentQuestionIndex={currentQuestionIndex}
			answers={answers}
			gameStarted={gameStarted}
			timeAdjustSeconds={timeAdjustSeconds}
			onGameStart={handleGameStart}
			onBack={handleGameplayBack}
			onTimeOut={handleTimeOut}
			laneLinesCount={laneLinesCount}
			question={questionPayload}
			answerResolution={answerResolution}
			onAnswerRequest={handleAnswerRequest}
			onRoundExitComplete={handleRoundExitComplete}
			onSoundEffect={playSoundEffect}
			onImagePreview={onImagePreview ?? onPreviewImage}
		/>
	);
};

const LaneChooserGame = ({ audioOn, onAudioChange, themeKey, ...gameProps }: LaneChooserGameProps) => {
	const resolvedThemeKey = themeKey ?? gameProps.gameData?.free_play_theme;

	return (
		<GameStyleBoundary>
			<GameAudioProvider audioOn={audioOn} onAudioChange={onAudioChange}>
				<LaneChooserThemeProvider themeKey={resolvedThemeKey}>
					<LaneChooserGameInner {...gameProps} />
				</LaneChooserThemeProvider>
			</GameAudioProvider>
		</GameStyleBoundary>
	);
};

export default LaneChooserGame;
